import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ReferenceLine,
} from 'recharts';
import KPICard from '../common/KPICard';
import SectionLabel from '../common/SectionLabel';
import { formatCurrency, formatPercent } from '../../utils/formatting';
import { useClients } from '../../context/ClientsContext';
import { useHouseholdDetail } from '../../context/HouseholdDetailContext';
import { isClientActiveInYear, getClientRevenueComponents, getClientRevenueForYear } from '../../utils/clientDataLoader';
import { isInternalHousehold } from '../../../lib/households.js';

// Division filter options
const DIVISIONS = [
  { key: 'all', label: 'All' },
  { key: 'bwm', label: 'BWM' },
  { key: 'bts', label: 'BTS' },
  { key: 'bia', label: 'BIA' },
];

// Division display colors (Tailwind classes)
const DIVISION_COLORS = {
  bwm: 'bg-bullogic-blue',
  bts: 'bg-deep-teal',
  bia: 'bg-sky-blue',
};

// Service tier options grouped by division
const SERVICE_TIERS_BY_DIVISION = {
  bwm: [
    { value: 'guided-wealth', label: 'Guided Wealth' },
    { value: 'total-wealth', label: 'Total Wealth' },
    { value: 'investment-only', label: 'Investment Only' },
    { value: 'tax-plan', label: 'Tax Plan' },
  ],
  bts: [
    { value: 'tax-individual', label: 'Tax Prep (Individual)' },
    { value: 'tax-business', label: 'Tax Prep (Business)' },
    { value: 'business-package', label: 'Business Package' },
  ],
  bia: [
    { value: 'insurance', label: 'Insurance' },
  ],
};

// Human-readable tier labels
const TIER_LABELS = {
  'guided-wealth': 'Guided Wealth',
  'total-wealth': 'Total Wealth',
  'investment-only': 'Investment Only',
  'tax-plan': 'Tax Plan',
  'tax-individual': 'Tax Prep (Individual)',
  'tax-business': 'Tax Prep (Business)',
  'business-package': 'Business Package',
  'insurance': 'Insurance',
};

// Generate a unique client ID
function generateId() {
  return 'client-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
}


// Get the revenue value for display.
// BWM: show monthly if they pay monthly. Otherwise fall through to annual,
//   then taxPlanFee (legacy: standalone Tax Plan tier clients sometimes
//   have the fee in taxPlanFee instead of annualRevenue, depending on
//   when they were added). Without the taxPlanFee fallback, a Tax Plan
//   tier client with $12,500 in taxPlanFee + 0 elsewhere displays as $0.
// BTS/BIA: always show annual.
function getDisplayRevenue(client) {
  if (client.division === 'bwm') {
    if (client.monthlyRevenue > 0) return client.monthlyRevenue;
    if (client.annualRevenue > 0) return client.annualRevenue;
    return client.taxPlanFee || 0;
  }
  return client.annualRevenue || 0;
}

// Get a label for how the revenue is displayed (for the value suffix)
function getRevenueLabel(client) {
  if (client.division === 'bwm') {
    if (client.monthlyRevenue > 0) return '/mo';
    if (client.annualRevenue > 0 || client.taxPlanFee > 0) return '/yr';
    return '';
  }
  return '/yr';
}

// Get annual revenue for all calculations (concentration, KPIs, sorting)
// Uses annualRevenue as the source of truth (from the book/spreadsheet).
// Falls back to monthly * 12 only if annualRevenue is not set.
// Base revenue (subscription + tax prep + insurance) excluding investment fees
function getBaseRevenue(client) {
  let base = client.annualRevenue || 0;
  if (base === 0) {
    const monthly = client.monthlyRevenue || 0;
    base = monthly * 12;
  }
  return base;
}

// Total revenue including investment management fees and tax plan (BWM only)
function getAnnualizedRevenue(client) {
  const base = getBaseRevenue(client);
  if (client.division === 'bwm') {
    return base + (client.investmentFeeAnnual || 0) + (client.taxPlanFee || 0);
  }
  return base;
}

// Revenue label for the table header based on filtered division
function revenueColumnLabel(divisionFilter) {
  if (divisionFilter === 'bwm') return 'Monthly Revenue';
  if (divisionFilter === 'bts' || divisionFilter === 'bia') return 'Annual Revenue';
  return 'Revenue';
}

// Sortable table header component
function SortHeader({ label, field, sortBy, sortDir, onSort, align = 'left' }) {
  const isActive = sortBy === field;
  const arrow = isActive ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : '';
  return (
    <th
      className={`px-3 py-2 font-bold cursor-pointer select-none hover:text-sky-blue text-${align}`}
      onClick={() => onSort(field)}
    >
      {label}{arrow}
    </th>
  );
}

// Shared input/label styles (matching ProspectForm pattern)
const inputClass = 'w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[4px] focus:outline-none focus:border-bullogic-blue';
const labelClass = 'block text-[11px] font-bold uppercase tracking-[0.08em] text-navy mb-1';

// Format a name as "Last, First" for household naming
// "Fernando Alvarez-Gonzalez" -> "Alvarez-Gonzalez, Fernando"
// "Smith, John" -> "Smith, John" (already formatted)
// "Bullogic LLC" -> "Bullogic LLC" (company unchanged when company field provided)
function formatHouseholdName(name, company) {
  // If a company name is provided, use it as-is
  if (company && company.trim()) return company.trim();
  const trimmed = (name || '').trim();
  if (!trimmed) return '';
  // Already in "Last, First" format
  if (trimmed.includes(',')) return trimmed;
  // Otherwise split on whitespace, treat last token as last name
  const parts = trimmed.split(/\s+/);
  if (parts.length < 2) return trimmed; // single word, leave as-is
  const last = parts[parts.length - 1];
  const first = parts.slice(0, -1).join(' ');
  return `${last}, ${first}`;
}

// Empty client template for the add form
function emptyClient() {
  return {
    name: '',
    company: '',
    division: 'bwm',
    serviceTier: 'guided-wealth',
    startDate: new Date().toISOString().split('T')[0],
    status: 'Active',
    monthlyRevenue: '',
    annualRevenue: '',
    taxPlanFee: '',
    aum: '',
    churnedDate: null,
    notes: '',
  };
}

// ─── Price History Editor ────────────────────────────────────────────
// Records mid-year rate changes so revenue calculations reflect the
// partial-year math instead of always assuming current_rate × 12.
//
// UX:
//   - Shows existing entries (if any) with delete buttons
//   - Inline mini-form to add a new entry (effective date + new rate)
//   - On Add: appends entry to history AND updates the form's
//     monthlyRevenue field to the new rate (so the "current rate"
//     stays in sync)
//
// Storage: each entry is { effectiveDate: 'YYYY-MM-DD', monthlyRevenue: number }.
function PriceHistoryEditor({ history, currentRate, onChange }) {
  const [draftDate, setDraftDate] = useState('');
  const [draftRate, setDraftRate] = useState('');

  function addEntry(e) {
    e?.preventDefault();
    const rate = parseFloat(draftRate);
    if (!draftDate || isNaN(rate) || rate < 0) return;
    const next = [...(history || []), { effectiveDate: draftDate, monthlyRevenue: rate }]
      .sort((a, b) => (a.effectiveDate || '').localeCompare(b.effectiveDate || ''));
    onChange(next);
    setDraftDate('');
    setDraftRate('');
  }

  function removeEntry(idx) {
    const next = [...(history || [])];
    next.splice(idx, 1);
    onChange(next);
  }

  const sorted = [...(history || [])].sort((a, b) => (a.effectiveDate || '').localeCompare(b.effectiveDate || ''));

  return (
    <div className="border-t border-gray-200 pt-3 mt-3">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-deep-teal">Rate Change History</div>
        <div className="text-[10px] text-[#888]">Optional — only use if the rate changed mid-year</div>
      </div>

      {sorted.length > 0 && (
        <div className="space-y-1 mb-2">
          {sorted.map((entry, i) => (
            <div key={i} className="flex items-center gap-2 text-[12px] bg-bone/40 px-2 py-1.5 rounded-[3px]">
              <span className="text-[#666] w-28 flex-shrink-0">From {entry.effectiveDate}:</span>
              <span className="font-bold text-navy">${Number(entry.monthlyRevenue).toLocaleString()}/mo</span>
              <button
                type="button"
                onClick={() => removeEntry(i)}
                className="ml-auto text-[10px] font-bold text-rose hover:underline"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="block text-[10px] font-bold uppercase tracking-[0.08em] text-[#888] mb-0.5">Effective Date</label>
          <input
            type="date"
            value={draftDate}
            onChange={e => setDraftDate(e.target.value)}
            className="w-full px-2 py-1.5 text-[12px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue"
          />
        </div>
        <div className="flex-1">
          <label className="block text-[10px] font-bold uppercase tracking-[0.08em] text-[#888] mb-0.5">New Monthly Rate ($)</label>
          <input
            type="number"
            value={draftRate}
            onChange={e => setDraftRate(e.target.value)}
            placeholder={String(currentRate || '')}
            className="w-full px-2 py-1.5 text-[12px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue"
          />
        </div>
        <button
          type="button"
          onClick={addEntry}
          disabled={!draftDate || !draftRate}
          className={`px-3 py-1.5 text-[11px] font-bold rounded-[3px] ${
            !draftDate || !draftRate
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-deep-teal text-white hover:bg-deep-teal/85'
          }`}
        >
          Add change
        </button>
      </div>
      <div className="text-[10px] text-[#888] mt-2 italic">
        For mid-year rate changes (e.g., $250 → $750 effective May 1, 2026). Also update the Monthly Revenue field above to the new current rate so going-forward forecasts use it. Without entries here, all months use the current Monthly Revenue.
      </div>
    </div>
  );
}

// ─── Client Form ─────────────────────────────────────────────────────
function ClientForm({ onSave, onCancel, initial, existingHouseholds }) {
  const isEditing = !!initial?.id;
  const [form, setForm] = useState(() => {
    if (initial) return {
      ...initial,
      householdId: initial.householdId || '',
      householdName: initial.householdName || '',
      monthlyRevenue: initial.monthlyRevenue != null ? String(initial.monthlyRevenue) : '',
      annualRevenue: initial.annualRevenue != null ? String(initial.annualRevenue) : '',
      taxPlanFee: initial.taxPlanFee != null ? String(initial.taxPlanFee) : '',
      aum: initial.aum != null ? String(initial.aum) : '',
      priceHistory: Array.isArray(initial.priceHistory) ? initial.priceHistory : [],
    };
    return { ...emptyClient(), householdId: '', householdName: '' };
  });

  // When division changes, reset service tier to first option for that division
  useEffect(() => {
    const tiers = SERVICE_TIERS_BY_DIVISION[form.division] || [];
    const currentTierBelongs = tiers.some(t => t.value === form.serviceTier);
    if (!currentTierBelongs && tiers.length > 0) {
      setForm(prev => ({ ...prev, serviceTier: tiers[0].value }));
    }
  }, [form.division, form.serviceTier]);

  function update(field, value) {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      // Auto-sync annual revenue when monthly changes (for recurring clients)
      if (field === 'monthlyRevenue') {
        const monthly = parseFloat(value) || 0;
        if (monthly > 0) {
          next.annualRevenue = String(Math.round(monthly * 12 * 100) / 100);
        }
      }
      // Auto-stamp churnedDate when flipping to Churned (and clear when
      // flipping back to Active). User can override the date right after.
      if (field === 'status') {
        if (value === 'Churned' && !prev.churnedDate) {
          next.churnedDate = new Date().toISOString().split('T')[0];
        } else if (value === 'Active') {
          next.churnedDate = null;
        }
      }
      return next;
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    const monthly = parseFloat(form.monthlyRevenue) || 0;
    const annual = parseFloat(form.annualRevenue) || 0;
    // If no household selected, create a new one from the client name
    const hhId = form.householdId || `hh-manual-${Date.now()}`;
    // Format household name as "Last, First" unless using a company name
    const hhName = form.householdName || formatHouseholdName(form.name, form.company);
    // Keep churnedYear in sync with churnedDate so the FP retention KPI
    // and the pro-ration helpers don't disagree about when this churn
    // happened. Active clients clear both.
    let churnedYear = form.churnedYear ?? null;
    let churnedDate = form.churnedDate || null;
    if (form.status !== 'Churned') {
      churnedYear = null;
      churnedDate = null;
    } else if (churnedDate) {
      churnedYear = new Date(churnedDate).getFullYear();
    } else if (!churnedYear) {
      const today = new Date();
      churnedYear = today.getFullYear();
      churnedDate = today.toISOString().split('T')[0];
    }
    onSave({
      ...form,
      name: form.name.trim(),
      company: form.company?.trim() || '',
      householdId: hhId,
      householdName: hhName,
      monthlyRevenue: monthly,
      annualRevenue: monthly > 0 ? Math.max(annual, monthly * 12) : annual,
      taxPlanFee: parseFloat(form.taxPlanFee) || 0,
      aum: parseFloat(form.aum) || 0,
      churnedYear,
      churnedDate,
      notes: form.notes?.trim() || '',
      // Pass through any priceHistory edits made via the rate-change UI.
      priceHistory: Array.isArray(form.priceHistory) ? form.priceHistory : [],
    });
  }

  const availableTiers = SERVICE_TIERS_BY_DIVISION[form.division] || [];
  const isBWM = form.division === 'bwm';

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 border border-bullogic-blue/30">
      <h3 className="text-[14px] font-bold text-navy mb-4">
        {isEditing ? 'Edit Client' : 'Add Client'}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Row 1: Name, Company, Household */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Name *</label>
            <input type="text" value={form.name} onChange={e => update('name', e.target.value)}
              placeholder="Client name" className={inputClass} required />
          </div>
          <div>
            <label className={labelClass}>Company (optional)</label>
            <input type="text" value={form.company} onChange={e => update('company', e.target.value)}
              placeholder="Company name" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Household</label>
            <select value={form.householdId} onChange={e => {
              const val = e.target.value;
              const selected = existingHouseholds?.find(h => h.id === val);
              if (selected) {
                // Existing household selected - use its name
                setForm(prev => ({ ...prev, householdId: selected.id, householdName: selected.name }));
              } else {
                // "New household" selected - clear both so form name gets used
                setForm(prev => ({ ...prev, householdId: '', householdName: '' }));
              }
            }} className={inputClass}>
              <option value="">New household (from name)</option>
              {(existingHouseholds || []).map(h => (
                <option key={h.id} value={h.id}>{h.name} ({h.entries.length} members)</option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 2: Division, Service Tier */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Division</label>
            <select value={form.division} onChange={e => update('division', e.target.value)} className={inputClass}>
              <option value="bwm">BWM - Wealth Management</option>
              <option value="bts">BTS - Tax Services</option>
              <option value="bia">BIA - Insurance</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Service Tier</label>
            <select value={form.serviceTier} onChange={e => update('serviceTier', e.target.value)} className={inputClass}>
              {availableTiers.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 3: Start Date, Status, Churn Date */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Start Date</label>
            <input type="date" value={form.startDate} onChange={e => update('startDate', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Status</label>
            <select value={form.status} onChange={e => update('status', e.target.value)} className={inputClass}>
              <option value="Active">Active</option>
              <option value="Churned">Churned</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Churn Date</label>
            <input
              type="date"
              value={form.churnedDate || ''}
              onChange={e => update('churnedDate', e.target.value || null)}
              disabled={form.status !== 'Churned'}
              className={inputClass + (form.status !== 'Churned' ? ' opacity-50 cursor-not-allowed' : '')}
            />
            <div className="text-[10px] text-[#888] mt-1">Drives revenue pro-ration. Only used when Churned.</div>
          </div>
        </div>

        {/* Row 4: Revenue fields */}
        <div className="border-t border-gray-200 pt-3 mt-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-deep-teal mb-2">Revenue Details</div>
          <div className="grid grid-cols-3 gap-3">
            {isBWM && (
              <div>
                <label className={labelClass}>Monthly Revenue ($)</label>
                <input type="number" value={form.monthlyRevenue} onChange={e => update('monthlyRevenue', e.target.value)}
                  placeholder="650" className={inputClass} />
              </div>
            )}
            <div>
              <label className={labelClass}>Annual Revenue ($)</label>
              <input type="number" value={form.annualRevenue} onChange={e => update('annualRevenue', e.target.value)}
                placeholder={isBWM ? '7800' : '1500'} className={inputClass} />
            </div>
            {isBWM && (
              <div>
                <label className={labelClass}>AUM ($)</label>
                <input type="number" value={form.aum} onChange={e => update('aum', e.target.value)}
                  placeholder="500000" className={inputClass} />
              </div>
            )}
          </div>
          {isBWM && (
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div>
                <label className={labelClass}>Tax Plan Fee ($) <span className="font-normal text-[#999]">one-time</span></label>
                <input type="number" value={form.taxPlanFee} onChange={e => update('taxPlanFee', e.target.value)}
                  placeholder="5000" className={inputClass} />
              </div>
            </div>
          )}
        </div>

        {/* Rate Change History — only relevant for monthly recurring clients.
            Lets the user record mid-year price increases/decreases so revenue
            calculations show the actual partial-year amount, not the wrong
            "current rate × 12" assumption. */}
        {parseFloat(form.monthlyRevenue) > 0 && (
          <PriceHistoryEditor
            history={form.priceHistory || []}
            currentRate={parseFloat(form.monthlyRevenue) || 0}
            onChange={(next) => setForm(prev => ({ ...prev, priceHistory: next }))}
          />
        )}

        {/* Notes */}
        <div>
          <label className={labelClass}>Notes (optional)</label>
          <textarea value={form.notes} onChange={e => update('notes', e.target.value)}
            placeholder="Any relevant details..." rows={2} className={inputClass} />
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button type="submit"
            className="px-4 py-2 text-[12px] font-bold text-white bg-bullogic-blue rounded-[4px] hover:bg-bullogic-blue/90">
            {isEditing ? 'Update Client' : 'Add Client'}
          </button>
          <button type="button" onClick={onCancel}
            className="px-4 py-2 text-[12px] font-bold text-[#888] bg-gray-100 rounded-[4px] hover:bg-gray-200">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────
export default function BWMClients() {
  // SOURCE OF TRUTH: ClientsContext / Supabase. No local mirror — that
  // pattern caused a sync race where a stuck writePendingRef could leave
  // local state stale, and a render with stale state could persist []
  // back to the server. We now read ctxClients directly and route every
  // mutation through replaceClients() so the same array view drives
  // both the UI and the persistence.
  const { clients, replaceClients, saveOne, removeOne } = useClients();

  // setClients keeps the legacy setState API (function or array updater)
  // so the existing merge/split/rename/etc code paths don't need to
  // change. The actual write is gated by a safety check: if a single
  // mutation would wipe ALL clients, we refuse and log loudly. Real
  // operations (merge, edit, delete one) never produce an empty array
  // when the source had data.
  const setClients = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(clients) : updater;
    if (!Array.isArray(next)) {
      console.error('[BWMClients] setClients received non-array, ignoring:', next);
      return;
    }
    if (next.length === 0 && clients.length > 0) {
      console.error('[BWMClients] Refusing to wipe all', clients.length, 'clients via setClients. Stack:', new Error().stack);
      return;
    }
    replaceClients(next).catch(err => console.error('[BWMClients] persist failed:', err));
  }, [clients, replaceClients]);

  const [divisionFilter, setDivisionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('household'); // 'individual' | 'household'
  const [mergeSource, setMergeSource] = useState(null); // householdId being merged FROM
  // Modal still opens via the global HouseholdDetail provider for legacy
  // call sites (Insurance, Pipeline). The row click on this page now
  // navigates to /crm/households/:id (Phase B). Phase E removes the modal.
  const { openHouseholdId, openHousehold, closeHousehold } = useHouseholdDetail();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('revenue');
  const [sortDir, setSortDir] = useState('desc');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  // (Persistence handled by ClientsProvider via replaceClients above.)
  // We previously did a focus-refresh from the server here so other-machine
  // edits showed up automatically — but that races against in-flight
  // mutations and was reverting fresh merges. Removed for correctness; the
  // user can refresh the page when they want to pull other machines' edits.

  // ─── CRUD operations ────────────────────────────────────────────
  //
  // ALL mutations bypass the local setClients wrapper. That wrapper closes
  // over `clients` from its definition render; useCallback([]) here would
  // capture the first render's wrapper — at which point clients was []
  // (still loading). The result was the wrapper running updaters against
  // an empty array, producing [], and replaceClients refusing to wipe.
  // The visual flip was React's optimistic state, never persisted.
  //
  // The fix is twofold for each mutation:
  //   1. Use saveOne / removeOne / replaceClients from context directly
  //   2. List `clients`, `saveOne`, etc. as deps so the closure stays fresh
  //
  // Single-client edits → saveOne (does PUT for existing, POST for new).
  // Single-client deletes → removeOne (DELETE).
  // Multi-row updates (household merge/rename) → replaceClients with the
  // full updated array (uses bulk-upsert).

  const addClient = useCallback(async (data) => {
    const newClient = { ...data, id: generateId() };
    try {
      await saveOne(newClient);
    } catch (e) {
      console.error('[BWMClients] addClient failed:', e);
      window.alert(`Failed to add client: ${e.message}`);
    }
    setShowForm(false);
  }, [saveOne]);

  const updateClient = useCallback(async (data) => {
    const target = clients.find(c => c.id === data.id);
    if (!target) {
      console.warn('[BWMClients] updateClient: no matching client for id', data.id);
      return;
    }
    try {
      await saveOne({ ...target, ...data });
    } catch (e) {
      console.error('[BWMClients] updateClient failed:', e);
      window.alert(`Failed to update "${target.name || data.name || data.id}": ${e.message}`);
    }
    setEditingClient(null);
    setShowForm(false);
  }, [clients, saveOne]);

  // Toggle Active <-> Churned. Stamps churnedYear (year-resolution, drives
  // the FP retention KPI) AND churnedDate (day-resolution, drives revenue
  // pro-ration). Defaults to today; backdate via the edit form if needed.
  // Reactivating clears both.
  const toggleStatus = useCallback(async (id) => {
    const target = clients.find(c => c.id === id);
    if (!target) return;
    const becomingChurned = target.status === 'Active';
    const today = new Date();
    const updated = {
      ...target,
      status: becomingChurned ? 'Churned' : 'Active',
      churnedYear: becomingChurned ? today.getFullYear() : null,
      churnedDate: becomingChurned ? today.toISOString().split('T')[0] : null,
    };
    try {
      await saveOne(updated);
    } catch (e) {
      console.error('[BWMClients] toggleStatus failed:', e);
      window.alert(`Failed to update status for "${target.name}": ${e.message}`);
    }
  }, [clients, saveOne]);

  const removeClient = useCallback(async (id) => {
    const target = clients.find(c => c.id === id);
    try {
      await removeOne(id);
    } catch (e) {
      console.error('[BWMClients] removeClient failed:', e);
      window.alert(`Failed to remove "${target?.name || id}": ${e.message}`);
    }
  }, [clients, removeOne]);

  // ─── Household management ─────────────────────────────────────
  // Split: remove a single client from a household into its own. Affects
  // ONE client, so saveOne is sufficient.
  const splitFromHousehold = useCallback(async (clientId) => {
    const target = clients.find(c => c.id === clientId);
    if (!target) return;
    const newHhId = `hh-manual-${Date.now()}`;
    try {
      await saveOne({ ...target, householdId: newHhId, householdName: target.name });
    } catch (e) {
      console.error('[BWMClients] splitFromHousehold failed:', e);
      window.alert(`Failed to split "${target.name}" from household: ${e.message}`);
    }
  }, [clients, saveOne]);

  // Merge: combine all clients from sourceHhId into targetHhId. Touches
  // multiple clients, so we use replaceClients (bulk-upsert).
  const mergeHouseholds = useCallback(async (targetHhId, targetName) => {
    if (!mergeSource || mergeSource === targetHhId) return;
    const updated = clients.map(c => {
      if (c.householdId !== mergeSource) return c;
      return { ...c, householdId: targetHhId, householdName: targetName };
    });
    try {
      await replaceClients(updated);
    } catch (e) {
      console.error('[BWMClients] mergeHouseholds failed:', e);
      window.alert(`Failed to merge household: ${e.message}`);
    }
    setMergeSource(null);
  }, [clients, mergeSource, replaceClients]);

  // Rename a household. Touches every client in that household.
  const renameHousehold = useCallback(async (hhId, newName) => {
    const updated = clients.map(c => {
      if (c.householdId !== hhId) return c;
      return { ...c, householdName: newName };
    });
    try {
      await replaceClients(updated);
    } catch (e) {
      console.error('[BWMClients] renameHousehold failed:', e);
      window.alert(`Failed to rename household: ${e.message}`);
    }
  }, [clients, replaceClients]);

  // ─── Filtered clients ───────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = clients;
    if (divisionFilter !== 'all') result = result.filter(c => c.division === divisionFilter);
    if (statusFilter === 'active') result = result.filter(c => c.status === 'Active');
    else if (statusFilter === 'churned') result = result.filter(c => c.status === 'Churned');
    if (typeFilter === 'bookkeeping') result = result.filter(c => c.clientType === 'bookkeeping');
    else if (typeFilter === 'tax-prep') result = result.filter(c => c.clientType === 'tax-prep');
    else if (typeFilter === 'cross-division') result = result.filter(c => c.divisions && c.divisions.length > 1);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.company || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [clients, divisionFilter, statusFilter, typeFilter, searchQuery]);

  // ─── KPI calculations (computed every render to ensure freshness) ──
  const activeFiltered = filtered.filter(c => c.status === 'Active');
  const churnedFiltered = filtered.filter(c => c.status === 'Churned');
  // Revenue totals: active clients + churned BWM clients (whose revenue is partial-year amounts).
  // Churned BTS clients keep their old full-year revenue values so we exclude them to avoid
  // double-counting historical revenue.
  const allByDivision = clients.filter(c =>
    (divisionFilter === 'all' || c.division === divisionFilter)
  );
  const revenueContributors = allByDivision.filter(c => {
    const hasRevenue = getBaseRevenue(c) > 0 || (c.investmentFeeAnnual || 0) > 0 || (c.taxPlanFee || 0) > 0;
    if (!hasRevenue) return false;
    // Active clients always included
    if (c.status === 'Active') return true;
    // Churned BWM clients included (their annualRevenue is the partial-year amount they actually earned)
    if (c.status === 'Churned' && c.division === 'bwm') return true;
    // Other churned clients (BTS, BIA) excluded to avoid counting stale full-year revenue
    return false;
  });
  // Year-aware revenue: future-start clients contribute $0; mid-year churns
  // contribute monthly × monthsActive (sum-of-months for recurring) + pro-rated
  // BWM extras. See getClientRevenueComponents in clientDataLoader for the
  // full math. Use revenueContributors as the ever-had-revenue universe;
  // contributors2026 is who actually contributes to this year.
  const revenueContributors2026 = revenueContributors.filter(c => isClientActiveInYear(c, 2026));
  // Memoize components for every active client so the rollups below don't
  // recompute. Covers both revenueContributors2026 and activeRevenueIn2026.
  const componentsByClient = new Map();
  function compsFor(c) {
    if (!componentsByClient.has(c.id)) {
      componentsByClient.set(c.id, getClientRevenueComponents(c, 2026));
    }
    return componentsByClient.get(c.id);
  }
  const totalRevenue = revenueContributors2026.reduce((sum, c) => sum + compsFor(c).total, 0);
  // Retention rate = active / ever-existed (active + churned) within the
  // current division + type filters. We deliberately IGNORE the status
  // filter here — otherwise filtering to "Active only" trivially yields
  // 100% retention (active / active = 1). The retention universe should
  // mirror the division/type slice the user is looking at, but always
  // compare against the full active+churned base for that slice.
  const retentionUniverse = clients.filter(c => {
    if (divisionFilter !== 'all' && c.division !== divisionFilter) return false;
    if (typeFilter === 'bookkeeping' && c.clientType !== 'bookkeeping') return false;
    if (typeFilter === 'tax-prep' && c.clientType !== 'tax-prep') return false;
    if (typeFilter === 'cross-division' && !(c.divisions && c.divisions.length > 1)) return false;
    return c.status === 'Active' || c.status === 'Churned';
  });
  const retentionActiveCount = retentionUniverse.filter(c => c.status === 'Active').length;
  const retentionRate = retentionUniverse.length > 0
    ? retentionActiveCount / retentionUniverse.length
    : 0;
  const activeRevenueIn2026 = activeFiltered.filter(c => isClientActiveInYear(c, 2026));
  const avgRevenue = activeRevenueIn2026.length > 0
    ? activeRevenueIn2026.reduce((sum, c) => sum + getClientRevenueForYear(c, 2026), 0) / activeRevenueIn2026.length : 0;
  const sortedForConc = [...activeRevenueIn2026]
    .sort((a, b) => getClientRevenueForYear(b, 2026) - getClientRevenueForYear(a, 2026));
  const top5Rev = sortedForConc.slice(0, 5).reduce((sum, c) => sum + getClientRevenueForYear(c, 2026), 0);
  const top5Pct = totalRevenue > 0 ? top5Rev / totalRevenue : 0;
  // Subscription = recurring BWM (Guided/Total Wealth). Explicitly excludes
  // Tax Plan tier even though those have base > 0 — Tax Plan is a one-time
  // engagement that already gets counted in taxPlanRevenue (via fromBase).
  // Without this exclusion, Tax Plan tier base double-counts in the
  // "Total Annual Revenue" subtitle: once as subscription, once as tax plan.
  const isSubscription = (c) => c.division === 'bwm' && c.serviceTier !== 'tax-plan' && getBaseRevenue(c) > 0;
  // Subscription / one-time use the year-aware base (not the full annual).
  const subscriptionRevenue = revenueContributors2026.filter(c => isSubscription(c))
    .reduce((sum, c) => sum + compsFor(c).base, 0);
  const oneTimeRevenue = revenueContributors2026
    .filter(c => !isSubscription(c) && c.division !== 'bwm' && getBaseRevenue(c) > 0)
    .reduce((sum, c) => sum + compsFor(c).base, 0);
  const investmentRevenue = activeRevenueIn2026.filter(c => c.division === 'bwm')
    .reduce((sum, c) => sum + compsFor(c).investmentFee, 0);
  // Tax Plan revenue includes:
  //   1. The taxPlanFee extra on BWM Guided/Total Wealth clients
  //      (one-time tax plan tacked on to a recurring relationship)
  //   2. The base revenue of standalone Tax Plan tier clients
  //      (one-time-only engagements where annualRevenue holds the fee
  //      and no taxPlanFee field is set). Without this, a $10K Tax Plan
  //      client shows correctly in Total Revenue but not in the
  //      dedicated Tax Plan KPI.
  const taxPlanRevenue = activeRevenueIn2026.filter(c => c.division === 'bwm')
    .reduce((sum, c) => {
      const comps = compsFor(c);
      const fromTaxPlanFee = comps.taxPlanFee;
      const fromBase = c.serviceTier === 'tax-plan' ? comps.base : 0;
      return sum + fromTaxPlanFee + fromBase;
    }, 0);

  // ─── Export functions ──────────────────────────────────────────
  function downloadCSV(filename, csvContent) {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportIndividualCSV() {
    const headers = ['Name', 'Company', 'Division', 'Service Tier', 'Status', 'Monthly Revenue', 'Annual Revenue', 'Tax Plan Fee', 'Investment Fee (Annual)', 'AUM', 'Household', 'Client Type', 'Start Date', 'Notes'];
    const rows = filtered.map(c => [
      c.name, c.company || '', (c.division || '').toUpperCase(), c.serviceTier || '', c.status,
      c.monthlyRevenue || 0, c.annualRevenue || 0, c.taxPlanFee || 0, c.investmentFeeAnnual || 0, c.aum || 0,
      c.householdName || '', c.clientType || '', c.startDate || '', (c.notes || '').replace(/,/g, ';'),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    downloadCSV('bullogic_clients.csv', csv);
  }

  function exportHouseholdCSV() {
    // Year-aware totals so the export matches what's displayed in the UI
    // (mid-year churns pro-rate, future-starts contribute $0).
    const hh = allHouseholds.map(h => {
      const active = h.entries.filter(e => e.status === 'Active' && isClientActiveInYear(e, 2026));
      const bwm = active.filter(e => e.division === 'bwm').reduce((s, e) => s + getClientRevenueForYear(e, 2026), 0);
      const bts = active.filter(e => e.division === 'bts').reduce((s, e) => s + getClientRevenueForYear(e, 2026), 0);
      const bia = active.filter(e => e.division === 'bia').reduce((s, e) => s + getClientRevenueForYear(e, 2026), 0);
      return [h.name, h.entries.length, active.length, bwm, bts, bia, bwm + bts + bia,
        [...new Set(h.entries.map(e => e.division.toUpperCase()))].join('+')];
    });
    const headers = ['Household', 'Total Members', 'Active Members', 'BWM Revenue', 'BTS Revenue', 'BIA Revenue', 'Total Revenue', 'Divisions'];
    const csv = [headers.join(','), ...hh.map(r => r.join(','))].join('\n');
    downloadCSV('bullogic_households.csv', csv);
  }

  // ─── All households (for the form dropdown, always computed) ──
  const allHouseholds = useMemo(() => {
    const groups = {};
    for (const c of clients) {
      if (isInternalHousehold(c)) continue;   // hh-firm is not a client household (lib/households.js)
      const key = c.householdId || c.name.toLowerCase().trim();
      if (!groups[key]) groups[key] = { name: c.householdName || c.name, entries: [], id: key };
      groups[key].entries.push(c);
    }
    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  }, [clients]);

  // ─── Household view: group clients by householdId ──
  const households = (() => {
    if (viewMode !== 'household') return null;
    // Group ALL clients by householdId (or name fallback)
    const groups = {};
    for (const c of clients) {
      if (isInternalHousehold(c)) continue;   // hh-firm is not a client household (lib/households.js)
      const key = c.householdId || c.name.toLowerCase().trim();
      if (!groups[key]) groups[key] = { name: c.householdName || c.name, entries: [], id: key };
      groups[key].entries.push(c);
    }

    // Build household rows
    let result = Object.values(groups).map(g => {
      const activeDivs = [...new Set(g.entries.filter(e => e.status === 'Active').map(e => e.division))].sort();
      const isActive = activeDivs.length > 0;

      // Per-household revenue rollups. Year + churn aware:
      //   - Future-start entries contribute $0 (started after 2026).
      //   - Mid-year churns contribute monthly × monthsActive for recurring,
      //     pro-rated for annual-only and BWM extras.
      //   - Churned BTS/BIA entries are excluded by the status filter
      //     (their annual revenue is no longer current-year truth).
      //   - BWM churned entries: REVENUE row excludes them too here, so
      //     each household column shows what's still flowing this year.
      // Note: pulls components per entry so we can split by category.
      const activeEntries = g.entries.filter(e => e.status === 'Active' && isClientActiveInYear(e, 2026));
      const entryComps = activeEntries.map(e => ({ e, c: getClientRevenueComponents(e, 2026) }));
      const taxPlanRev = entryComps.reduce((s, { c }) => s + c.taxPlanFee, 0);
      const bwmRev = entryComps.filter(({ e }) => e.division === 'bwm').reduce((s, { c }) => s + c.base, 0) + taxPlanRev;
      const btsRev = entryComps.filter(({ e }) => e.division === 'bts').reduce((s, { c }) => s + c.base, 0);
      const biaRev = entryComps.filter(({ e }) => e.division === 'bia').reduce((s, { c }) => s + c.base, 0);
      const investmentRev = entryComps.reduce((s, { c }) => s + c.investmentFee, 0);
      const totalRev = bwmRev + btsRev + biaRev + investmentRev;

      return {
        id: g.id,
        name: g.name,
        divisions: activeDivs,
        isActive,
        entries: g.entries,
        bwmRev, btsRev, biaRev, investmentRev, totalRev,
        startDate: g.entries.reduce((earliest, e) => !earliest || e.startDate < earliest ? e.startDate : earliest, null),
      };
    });

    // Apply filters
    if (statusFilter === 'active') result = result.filter(h => h.isActive);
    else if (statusFilter === 'churned') result = result.filter(h => !h.isActive);
    if (divisionFilter !== 'all') result = result.filter(h => h.divisions.includes(divisionFilter));
    if (typeFilter === 'cross-division') result = result.filter(h => h.divisions.length > 1);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(h => h.name.toLowerCase().includes(q));
    }

    // Sort
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortBy === 'name') result.sort((a, b) => dir * a.name.localeCompare(b.name));
    else result.sort((a, b) => dir * (a.totalRev - b.totalRev));

    return result;
  })();

  // ─── Sorting ────────────────────────────────────────────────────
  function handleSort(field) {
    if (sortBy === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir(field === 'name' ? 'asc' : 'desc');
    }
  }

  const sortedClients = useMemo(() => {
    const list = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortBy) {
      case 'name':
        return list.sort((a, b) => dir * a.name.localeCompare(b.name));
      case 'division':
        return list.sort((a, b) => dir * a.division.localeCompare(b.division));
      case 'tier':
        return list.sort((a, b) => dir * (a.serviceTier || '').localeCompare(b.serviceTier || ''));
      case 'date':
        return list.sort((a, b) => dir * (new Date(a.startDate) - new Date(b.startDate)));
      case 'status':
        return list.sort((a, b) => dir * a.status.localeCompare(b.status));
      case 'revenue':
      default:
        return list.sort((a, b) => dir * (getDisplayRevenue(a) - getDisplayRevenue(b)));
    }
  }, [filtered, sortBy, sortDir]);

  // ─── Concentration chart data ───────────────────────────────────
  const concentrationData = sortedForConc.slice(0, 15).map(c => ({
    name: c.name,
    revenue: getAnnualizedRevenue(c),
    pctOfTotal: totalRevenue > 0 ? getAnnualizedRevenue(c) / totalRevenue : 0,
  }));
  const concentrationThreshold = totalRevenue * 0.10;

  // ─── Form handlers ─────────────────────────────────────────────
  function openAddForm() {
    setEditingClient(null);
    setShowForm(true);
  }

  function openEditForm(client) {
    setEditingClient(client);
    setShowForm(true);
  }

  // Open the Add form pre-filled to attach a new service to an EXISTING
  // household. Called from HouseholdDetail's "Add Service" buttons.
  // The form treats this like a new client (no id) so handleFormSave
  // routes through addClient — but householdId / householdName are
  // locked to the chosen household, and division defaults to whatever
  // sub-tab the user clicked from.
  function openAddServiceForm({ householdId, householdName, division }) {
    setEditingClient({
      // No id => save creates a new entry attached to this household
      householdId,
      householdName,
      division: division || 'bia',
      name: householdName ? householdName.replace(/^([^,]+),\s*(.+)$/, '$2 $1').trim() : '',
      status: 'Active',
      startDate: new Date().toISOString().split('T')[0],
    });
    setShowForm(true);
  }

  function handleFormSave(data) {
    // Use id presence, not editingClient truthiness — openAddServiceForm
    // pre-fills editingClient with a partial template (no id) so the form
    // knows the household but should still create a new entry.
    if (editingClient?.id) {
      updateClient({ ...data, id: editingClient.id });
    } else {
      addClient(data);
    }
  }

  function handleFormCancel() {
    setShowForm(false);
    setEditingClient(null);
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex items-center justify-between">
        {/* Division filter */}
        <div className="flex items-center gap-2">
          {DIVISIONS.map(d => (
            <button
              key={d.key}
              onClick={() => setDivisionFilter(d.key)}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-[4px] transition-colors ${
                divisionFilter === d.key
                  ? 'bg-bullogic-blue text-white'
                  : 'bg-gray-100 text-[#888] hover:bg-gray-200'
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {/* View mode toggle */}
          <div className="flex gap-0.5 bg-gray-100 rounded-[4px] p-0.5">
            {[
              { key: 'individual', label: 'Individual' },
              { key: 'household', label: 'Household' },
            ].map(v => (
              <button
                key={v.key}
                onClick={() => setViewMode(v.key)}
                className={`px-3 py-1.5 text-[11px] font-bold rounded-[3px] transition-colors ${
                  viewMode === v.key ? 'bg-white text-navy shadow-sm' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
          {/* Type filter */}
          <div className="flex gap-0.5 bg-gray-100 rounded-[4px] p-0.5">
            {[
              { key: 'all', label: 'All Types' },
              { key: 'bookkeeping', label: 'Bookkeeping' },
              { key: 'tax-prep', label: 'Tax Prep' },
              { key: 'cross-division', label: 'BWM + BTS' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTypeFilter(t.key)}
                className={`px-3 py-1.5 text-[11px] font-bold rounded-[3px] transition-colors ${
                  typeFilter === t.key
                    ? 'bg-white text-navy shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {/* Status filter */}
          <div className="flex gap-0.5 bg-gray-100 rounded-[4px] p-0.5">
            {[
              { key: 'active', label: 'Active' },
              { key: 'churned', label: 'Churned' },
              { key: 'all', label: 'All' },
            ].map(s => (
              <button
                key={s.key}
                onClick={() => setStatusFilter(s.key)}
                className={`px-3 py-1.5 text-[11px] font-bold rounded-[3px] transition-colors ${
                  statusFilter === s.key
                    ? 'bg-white text-navy shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-5 gap-4">
        <KPICard
          label="Total Annual Revenue"
          value={formatCurrency(totalRevenue)}
          subtitle={[
            subscriptionRevenue > 0 && `${formatCurrency(subscriptionRevenue)} subscription`,
            investmentRevenue > 0 && `${formatCurrency(investmentRevenue)} AUM`,
            taxPlanRevenue > 0 && `${formatCurrency(taxPlanRevenue)} tax plan`,
            oneTimeRevenue > 0 && `${formatCurrency(oneTimeRevenue)} tax services`,
          ].filter(Boolean).join(' + ')}
        />
        <KPICard
          label="Active Clients"
          value={activeFiltered.length.toString()}
          subtitle={`${churnedFiltered.length} churned`}
        />
        <KPICard
          label="Retention Rate"
          value={formatPercent(retentionRate)}
          subtitle={`${activeFiltered.length} of ${filtered.length} total`}
        />
        <KPICard
          label="Avg Revenue / Client"
          value={formatCurrency(avgRevenue)}
          subtitle="annualized average"
        />
        <KPICard
          label="Top 5 Concentration"
          value={formatPercent(top5Pct)}
          subtitle={top5Pct > 0.40
            ? 'Above 40% threshold - high risk'
            : 'Below 40% threshold'
          }
          status={top5Pct > 0.40 ? 'warn' : null}
          statusLabel={top5Pct > 0.40 ? 'WATCH' : null}
        />
      </div>

      {/* Concentration warning */}
      {top5Pct > 0.40 && (
        <div className="bg-rose/10 border border-rose/30 rounded-[4px] px-4 py-3">
          <p className="text-[13px] text-rose font-bold">
            Concentration Risk: Top 5 clients represent {formatPercent(top5Pct)} of total revenue (threshold: 40%)
          </p>
        </div>
      )}

      {/* Add Client button + search */}
      {showForm ? (
        <ClientForm
          onSave={handleFormSave}
          onCancel={handleFormCancel}
          initial={editingClient}
          existingHouseholds={allHouseholds}
        />
      ) : (
        <div className="flex items-center gap-3">
          <button onClick={openAddForm}
            className="px-4 py-2 text-[12px] font-bold text-white bg-bullogic-blue rounded-[4px] hover:bg-bullogic-blue/90 shrink-0">
            + Add Client
          </button>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or company..."
            className="flex-1 px-3 py-2 text-[13px] border border-gray-300 rounded-[4px] focus:outline-none focus:border-bullogic-blue"
          />
          <button onClick={exportIndividualCSV}
            className="px-3 py-2 text-[11px] font-bold text-[#888] bg-gray-100 rounded-[4px] hover:bg-gray-200 shrink-0"
            title="Export individual clients as CSV">
            Export Clients
          </button>
          <button onClick={exportHouseholdCSV}
            className="px-3 py-2 text-[11px] font-bold text-[#888] bg-gray-100 rounded-[4px] hover:bg-gray-200 shrink-0"
            title="Export households as CSV">
            Export Households
          </button>
        </div>
      )}

      {/* ─── Household View ───────────────────────────────────── */}
      {viewMode === 'household' && households && (
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
          <div className="flex items-center justify-between mb-3">
            <SectionLabel>Household Revenue</SectionLabel>
            <span className="text-[11px] text-[#888]">{households.length} households</span>
          </div>
          {mergeSource && (
            <div className="bg-bullogic-blue/10 border border-bullogic-blue/30 rounded-[4px] px-4 py-2.5 mb-3 flex items-center justify-between">
              <span className="text-[12px] text-navy font-bold">
                Merge mode: Click a household to merge "{clients.find(c => c.householdId === mergeSource)?.householdName}" into it
              </span>
              <button onClick={() => setMergeSource(null)}
                className="text-[11px] font-bold text-[#888] bg-white px-3 py-1 rounded-[4px] hover:bg-gray-100">
                Cancel
              </button>
            </div>
          )}
          <div className="max-h-[600px] overflow-y-auto">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200">
                  <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-navy cursor-pointer hover:text-bullogic-blue"
                    onClick={() => handleSort('name')}>Client</th>
                  <th className="px-3 py-2 text-center text-[11px] font-bold uppercase tracking-wider text-navy">Divisions</th>
                  <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider text-navy">BWM</th>
                  <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider text-navy">BTS</th>
                  <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider text-navy">BIA</th>
                  <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider text-navy">Investments</th>
                  <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider text-navy cursor-pointer hover:text-bullogic-blue"
                    onClick={() => handleSort('revenue')}>Total Revenue</th>
                </tr>
              </thead>
              <tbody>
                {households.map((h) => {
                const isExpanded = openHouseholdId === h.id;
                const isMergeTarget = mergeSource && mergeSource !== h.id;
                return (
                  <React.Fragment key={h.id}>
                    <tr
                      className={`border-b border-gray-100 cursor-pointer transition-colors ${
                        isExpanded ? 'bg-bullogic-blue/5' : 'hover:bg-bone/50'
                      } ${!h.isActive ? 'opacity-50' : ''} ${isMergeTarget ? 'hover:bg-deep-teal/10' : ''}`}
                      onClick={() => {
                        if (mergeSource) {
                          mergeHouseholds(h.id, h.name);
                        } else {
                          // Phase B: row click navigates to the new per-household page.
                          // The legacy modal (HouseholdDetailProvider) is still wired
                          // up — other tabs (Pipeline etc.) continue to use it until
                          // Phase E migrates them too.
                          navigate(`/crm/households/${encodeURIComponent(h.id)}/overview`);
                        }
                      }}
                    >
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-[#aaa]" title="Click to open household card">▸</span>
                          <span className={`font-bold ${h.isActive ? 'text-navy' : 'text-rose line-through'}`}>{h.name}</span>
                          {h.entries.length > 1 && (
                            <span className="text-[9px] text-[#999] bg-gray-100 px-1.5 py-0.5 rounded-full">{h.entries.length}</span>
                          )}
                          {isMergeTarget && (
                            <span className="text-[9px] font-bold text-deep-teal bg-deep-teal/10 px-2 py-0.5 rounded-[3px]">Click to merge here</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {h.divisions.map(d => (
                            <span key={d} className={`inline-block px-1.5 py-0.5 rounded-[3px] text-[9px] font-bold text-white ${DIVISION_COLORS[d] || 'bg-gray-400'}`}>
                              {d.toUpperCase()}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className={`px-3 py-2.5 text-right ${h.bwmRev > 0 ? '' : 'text-[#ccc]'}`}>
                        {h.bwmRev > 0 ? formatCurrency(h.bwmRev) : '--'}
                      </td>
                      <td className={`px-3 py-2.5 text-right ${h.btsRev > 0 ? '' : 'text-[#ccc]'}`}>
                        {h.btsRev > 0 ? formatCurrency(h.btsRev) : '--'}
                      </td>
                      <td className={`px-3 py-2.5 text-right ${h.biaRev > 0 ? '' : 'text-[#ccc]'}`}>
                        {h.biaRev > 0 ? formatCurrency(h.biaRev) : '--'}
                      </td>
                      <td className={`px-3 py-2.5 text-right ${h.investmentRev > 0 ? '' : 'text-[#ccc]'}`}>
                        {h.investmentRev > 0 ? formatCurrency(h.investmentRev) : '--'}
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-deep-teal">
                        {formatCurrency(h.totalRev)}
                      </td>
                    </tr>
                    {/* Detail now opens as a modal — see HouseholdDetail render below the table. */}
                  </React.Fragment>
                );
              })}
                {/* Totals row */}
                <tr className="border-t-2 border-navy/20 bg-bone/50">
                  <td className="px-3 py-2.5 font-bold text-navy">Total</td>
                  <td className="px-3 py-2.5 text-center text-[11px] text-[#888]">{households.length}</td>
                  <td className="px-3 py-2.5 text-right font-bold">{formatCurrency(households.reduce((s, h) => s + h.bwmRev, 0))}</td>
                  <td className="px-3 py-2.5 text-right font-bold">{formatCurrency(households.reduce((s, h) => s + h.btsRev, 0))}</td>
                  <td className="px-3 py-2.5 text-right font-bold">{formatCurrency(households.reduce((s, h) => s + h.biaRev, 0))}</td>
                  <td className="px-3 py-2.5 text-right font-bold">{formatCurrency(households.reduce((s, h) => s + h.investmentRev, 0))}</td>
                  <td className="px-3 py-2.5 text-right font-bold text-deep-teal">{formatCurrency(households.reduce((s, h) => s + h.totalRev, 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── Individual View ──────────────────────────────────── */}
      {viewMode === 'individual' && (
      <div className="grid grid-cols-[1fr_400px] gap-6">
        {/* Client Table */}
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
          <SectionLabel>Client Roster</SectionLabel>

          <div className="max-h-[540px] overflow-y-auto">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0">
                <tr className="bg-navy text-white">
                  <SortHeader label="Name" field="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Division" field="division" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" />
                  <SortHeader label="Service Tier" field="tier" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Start Date" field="date" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" />
                  <SortHeader label={revenueColumnLabel(divisionFilter)} field="revenue" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                  <th className="px-3 py-2 text-right font-bold">% of Total</th>
                  <th className="px-3 py-2 text-center font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedClients.map((client, i) => {
                  const isChurned = client.status === 'Churned';
                  const rev = getDisplayRevenue(client);
                  const annualized = getAnnualizedRevenue(client);
                  const pctOfTotal = totalRevenue > 0 ? annualized / totalRevenue : 0;
                  const highConcentration = !isChurned && pctOfTotal > 0.10;

                  return (
                    <tr
                      key={client.id}
                      className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      {/* Name */}
                      <td className={`px-3 py-2 font-bold ${isChurned ? 'text-rose line-through' : 'text-navy'}`}>
                        {client.name}
                        {client.company && (
                          <span className="block text-[10px] font-normal text-[#888]">{client.company}</span>
                        )}
                      </td>

                      {/* Division badge + cross-division indicator */}
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-[4px] text-[10px] font-bold text-white ${DIVISION_COLORS[client.division] || 'bg-gray-400'}`}>
                          {client.division.toUpperCase()}
                        </span>
                        {client.divisions && client.divisions.length > 1 && (
                          <span className="inline-block ml-1 px-1.5 py-0.5 rounded-[3px] text-[8px] font-bold bg-navy/10 text-navy" title={`Also in ${client.divisions.filter(d => d !== client.division).map(d => d.toUpperCase()).join(', ')}`}>
                            +{client.divisions.filter(d => d !== client.division).map(d => d.toUpperCase()).join('+')}
                          </span>
                        )}
                      </td>

                      {/* Type / Service Tier */}
                      <td className={`px-3 py-2 ${isChurned ? 'text-rose' : ''}`}>
                        <span>{TIER_LABELS[client.serviceTier] || client.serviceTier}</span>
                        {client.clientType === 'bookkeeping' && (
                          <span className="block text-[9px] font-bold text-deep-teal">Recurring</span>
                        )}
                        {client.clientType === 'tax-prep' && client.division === 'bts' && (
                          <span className="block text-[9px] text-[#aaa]">One-time</span>
                        )}
                      </td>

                      {/* Start Date */}
                      <td className={`px-3 py-2 ${isChurned ? 'text-rose' : ''}`}>
                        {new Date(client.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </td>

                      {/* Status badge */}
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-[4px] text-[10px] font-bold text-white ${
                          isChurned ? 'bg-rose' : 'bg-deep-teal'
                        }`}>
                          {client.status}
                        </span>
                      </td>

                      {/* Revenue */}
                      <td className={`px-3 py-2 text-right ${isChurned ? 'text-rose' : ''}`}>
                        {formatCurrency(rev)}
                        <span className="text-[9px] text-[#aaa] ml-0.5">{getRevenueLabel(client)}</span>
                      </td>

                      {/* % of Total */}
                      <td className={`px-3 py-2 text-right ${highConcentration ? 'text-rose font-bold' : 'text-[#888]'}`}>
                        {isChurned ? '--' : formatPercent(pctOfTotal)}
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openEditForm(client)}
                            className="px-2 py-1 text-[10px] font-bold text-bullogic-blue border border-bullogic-blue/30 rounded-[4px] hover:bg-bullogic-blue/10"
                            title="Edit client"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => toggleStatus(client.id)}
                            className={`px-2 py-1 text-[10px] font-bold rounded-[4px] ${
                              isChurned
                                ? 'text-deep-teal border border-deep-teal/30 hover:bg-deep-teal/10'
                                : 'text-rose border border-rose/30 hover:bg-rose/10'
                            }`}
                            title={isChurned ? 'Reactivate' : 'Mark as churned'}
                          >
                            {isChurned ? 'Activate' : 'Churn'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {sortedClients.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-[#888] text-[13px]">
                      No clients found for this division.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Concentration Risk Chart */}
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
          <SectionLabel>Revenue Concentration (Top 15)</SectionLabel>
          <p className="text-[11px] font-light text-[#888] mb-4">
            Clients exceeding 10% of total revenue flagged in rose
          </p>

          {concentrationData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(200, concentrationData.length * 32)}>
              <BarChart data={concentrationData} layout="vertical" margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: '#888' }}
                  tickLine={false}
                  tickFormatter={(v) => `$${(v / 1000).toFixed(1)}K`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#1a1a2e' }}
                  tickLine={false}
                  width={70}
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ fontSize: 13, borderRadius: 4, border: '1px solid #e5e7eb' }}
                />
                <ReferenceLine
                  x={concentrationThreshold}
                  stroke="#FF6E83"
                  strokeDasharray="4 4"
                  label={{ value: '10%', position: 'top', fontSize: 10, fill: '#FF6E83' }}
                />
                <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                  {concentrationData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={entry.revenue > concentrationThreshold ? '#FF6E83' : '#2596BE'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-[13px] text-[#888] text-center py-10">No active clients to chart.</p>
          )}
        </div>
      </div>
      )}

      {/* Household detail modal mounted globally via HouseholdDetailHost
          inside HouseholdDetailProvider — opens via useHouseholdDetail(). */}
    </div>
  );
}
